import {
  Permissions,
  WILDCARD,
  hasPermission,
  holdsAll,
  type MyRoleGrant,
} from '@/models/permission';

import { type Role } from './role';

/** A Grant's scope: the three codes admin-api stores, '*' being the wildcard. */
export interface ScopeCodes {
  studio_code: string;
  game_code: string;
  sandbox_code: string;
}

const widens = (held: string, asked: string) =>
  held === WILDCARD || held === asked;

/** Whether `outer` contains `inner`, itself included: '*' widens, level by level. */
export const scopeContains = (outer: ScopeCodes, inner: ScopeCodes) =>
  widens(outer.studio_code, inner.studio_code) &&
  widens(outer.game_code, inner.game_code) &&
  widens(outer.sandbox_code, inner.sandbox_code);

/**
 * The member's Grant of this Role that already contains `scope`: one user never
 * holds a Role twice on nested scopes, so granting it there is refused (noop#359).
 */
export const coveringGrant = (
  member: MyRoleGrant[],
  roleId: string,
  scope: ScopeCodes,
) => member.find((grant) => grant.id === roleId && scopeContains(grant, scope));

/**
 * The member's narrower Grants of this Role that a Grant on `scope` replaces —
 * the person granting is told before it happens (noop#359).
 */
export const replacedGrants = (
  member: MyRoleGrant[],
  roleId: string,
  scope: ScopeCodes,
) =>
  member.filter(
    (grant) =>
      grant.id === roleId &&
      scopeContains(scope, grant) &&
      !scopeContains(grant, scope),
  );

/**
 * The scopes of `gameCode` worth probing for what the viewer can grant: All
 * Sandboxes, and every Sandbox one of their own Grants names, kept when they
 * manage permissions there. Any other Sandbox matches only the viewer's '*'
 * Grants, as All Sandboxes does, so it would add nothing — which is why this
 * reads /me rather than the Sandbox list, capped at one page.
 */
export const candidateScopes = (
  viewer: MyRoleGrant[],
  gameCode: string,
  studioCode?: string,
) =>
  [
    ...new Set([
      WILDCARD,
      ...viewer
        .filter(({ game_code }) => [gameCode, WILDCARD].includes(game_code))
        .map(({ sandbox_code }) => sandbox_code),
    ]),
  ].filter((sandboxCode) =>
    hasPermission(
      viewer,
      Permissions.users.managePermissions,
      gameCode,
      sandboxCode,
      studioCode,
    ),
  );

/**
 * Where the viewer can grant `role` on `gameCode`: the candidate scopes on which
 * they hold every permission it confers, All Sandboxes first when it is one.
 */
export const grantableScopes = (
  viewer: MyRoleGrant[],
  role: Role,
  gameCode: string,
  studioCode?: string,
) =>
  candidateScopes(viewer, gameCode, studioCode).filter((sandboxCode) =>
    holdsAll(viewer, role.permissions, gameCode, sandboxCode, studioCode),
  );

/** The Roles the viewer can grant on at least one scope of `gameCode`. */
export const grantableRoles = (
  viewer: MyRoleGrant[],
  roles: Role[],
  gameCode: string,
  studioCode?: string,
) =>
  roles.filter(
    (role) => grantableScopes(viewer, role, gameCode, studioCode).length > 0,
  );

/**
 * Whether the viewer can grant `role` on the whole Studio (S, *, *): managing
 * permissions there and holding every permission it confers there. Asking '*'
 * only matches a '*' Grant, so Games of the Studio never add up to it.
 */
export const grantableOnStudio = (
  viewer: MyRoleGrant[],
  role: Role,
  studioCode: string,
) =>
  hasPermission(
    viewer,
    Permissions.users.managePermissions,
    WILDCARD,
    WILDCARD,
    studioCode,
  ) && holdsAll(viewer, role.permissions, WILDCARD, WILDCARD, studioCode);

/**
 * The Games of `gameCodes` the viewer manages permissions on, led by '*' (All
 * games) when they manage the whole Studio: what the grant row offers first,
 * before any Role — the Role catalogue is read with the picked Game.
 */
export const manageableGames = (
  viewer: MyRoleGrant[],
  studioCode: string,
  gameCodes: string[],
) => [
  ...(hasPermission(
    viewer,
    Permissions.users.managePermissions,
    WILDCARD,
    WILDCARD,
    studioCode,
  )
    ? [WILDCARD]
    : []),
  ...gameCodes.filter(
    (gameCode) => candidateScopes(viewer, gameCode, studioCode).length > 0,
  ),
];
